import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const configEl = document.getElementById('dnd-gift-fulfillment-config');
if (configEl) {
  try {
    const config = JSON.parse(configEl.textContent);
    const giftVariantIds = config.giftVariantIds.map(Number).filter(id => Number.isSafeInteger(id) && id > 0);
    const giftsAvailable = config.giftsAvailable !== false && giftVariantIds.length === 3;
    const bundleIds = new Set(config.bundleVariantIds.map(Number));
    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const giftProp = '_dnd_first_order_gift';
    let busy = false;
    let checkingOut = false;
    let activeSync = Promise.resolve();
    let resolveSync;
    let queued = false;
    let timer;
    const getCart = async () => {
      const response = await fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Could not read DND cart');
      return response.json();
    };
    const mutate = async (path, body) => {
      const response = await fetch(root + path, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const error = new Error('DND cart update failed: ' + response.status);
        if (typeof payload?.description === 'string') error.customerMessage = payload.description;
        throw error;
      }
      return response.json();
    };
    const notifyCart = async () => {
      const latest = await getCart();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update', context: 'cart', lines: [],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(latest),
          detail: { itemCount: latest.item_count, items: latest.items, source: 'dnd-gift-fulfillment' }
        })
      }));
    };
    const sync = async () => {
      if (checkingOut) return;
      if (busy) { queued = true; return; }
      busy = true;
      activeSync = new Promise(resolve => { resolveSync = resolve; });
      try {
        const cart = await getCart();
        const qualifying = cart.items.some(item =>
          bundleIds.has(Number(item.variant_id)) && Boolean(item.selling_plan_allocation)
        );
        const giftLines = cart.items.filter(item => giftVariantIds.includes(Number(item.variant_id)));
        let changed = false;
        if (qualifying && giftsAvailable) {
          const existingIds = new Set(giftLines.map(item => Number(item.variant_id)));
          const missing = giftVariantIds.filter(id => !existingIds.has(id));
          if (missing.length) {
            await mutate('cart/add.js', { items: missing.map(id => ({ id, quantity: 1, properties: { [giftProp]: 'first-order' } })) });
            changed = true;
          }
        } else if (!qualifying) {
          const taggedGifts = giftLines.filter(item => item.properties?.[giftProp] === 'first-order');
          if (taggedGifts.length) {
            const updates = Object.fromEntries(taggedGifts.map(item => [item.key, 0]));
            await mutate('cart/update.js', { updates });
            changed = true;
          }
        }
        if (changed) await notifyCart();
      } catch (error) {
        console.warn('[DND] Gift synchronization was not completed:', error);
      } finally {
        busy = false;
        resolveSync();
        if (queued) { queued = false; scheduleSync(); }
      }
    };
    const scheduleSync = () => { clearTimeout(timer); timer = setTimeout(sync, 180); };
    document.addEventListener('submit', async event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form.dnd-product-form')) return;
      event.preventDefault();
      if (checkingOut) return;

      const submit = form.querySelector('button[type="submit"]');
      const feedback = form.querySelector('[data-dnd-checkout-error]');
      const selection = new FormData(form);
      const variantId = Number(selection.get('id'));
      const wantsSubscription = selection.get('purchase_option') !== 'one-time';
      const sellingPlanId = Number(selection.get('selling_plan'));
      const hasSellingPlan = Number.isSafeInteger(sellingPlanId) && sellingPlanId > 0;
      const quantity = Number(selection.get('quantity') || 1);
      if (!Number.isSafeInteger(variantId) || variantId <= 0 ||
          (wantsSubscription && !hasSellingPlan) ||
          (!wantsSubscription && (hasSellingPlan || form.dataset.dndOneTimeReady !== 'true')) ||
          !Number.isSafeInteger(quantity) || quantity < 1) {
        if (feedback) {
          feedback.textContent = feedback.dataset.dndCheckoutError;
          feedback.hidden = false;
        }
        return;
      }

      checkingOut = true;
      clearTimeout(timer);
      const wasDisabled = submit?.disabled;
      if (submit) { submit.disabled = true; submit.setAttribute('aria-busy', 'true'); }
      if (feedback) feedback.hidden = true;
      let redirected = false;
      try {
        await activeSync;
        const cart = await getCart();
        const existingGifts = new Set(cart.items.map(item => Number(item.variant_id)));
        const items = [{ id: variantId, quantity }];
        if (hasSellingPlan) items[0].selling_plan = sellingPlanId;
        const flavour = String(selection.get('properties[Flavour]') || '').trim();
        if (flavour) items[0].properties = { Flavour: flavour };
        const qualifying = (bundleIds.has(variantId) && hasSellingPlan) || cart.items.some(item =>
          bundleIds.has(Number(item.variant_id)) && Boolean(item.selling_plan_allocation)
        );
        if (!qualifying) {
          const staleGifts = cart.items.filter(item => giftVariantIds.includes(Number(item.variant_id)) && item.properties?.[giftProp] === 'first-order');
          if (staleGifts.length) await mutate('cart/update.js', { updates: Object.fromEntries(staleGifts.map(item => [item.key, 0])) });
        }
        if (qualifying && giftsAvailable) {
          for (const id of giftVariantIds) {
            if (!existingGifts.has(id)) items.push({ id, quantity: 1, properties: { [giftProp]: 'first-order' } });
          }
        }
        await mutate('cart/add.js', { items });
        window.location.assign(root + 'checkout');
        redirected = true;
      } catch (error) {
        if (feedback) {
          feedback.textContent = error.customerMessage || feedback.dataset.dndCheckoutError;
          feedback.hidden = false;
        }
        console.warn('[DND] Checkout was not opened:', error);
      } finally {
        if (!redirected) {
          checkingOut = false;
          if (submit) { submit.disabled = Boolean(wasDisabled); submit.removeAttribute('aria-busy'); }
          scheduleSync();
        }
      }
    });
    document.addEventListener(StandardEvents.cartLinesUpdate, event => {
      if (busy) return;
      Promise.resolve(event.promise).then(scheduleSync).catch(scheduleSync);
    });
    window.addEventListener('pageshow', scheduleSync);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
    else scheduleSync();
  } catch (error) {
    console.warn('[DND] Gift synchronization setup failed:', error);
  }
}

